import { ApolloError } from '@apollo/client';
import { User } from '@choco-admin/types';
import { useCallback } from 'react';
import { v4 as uuidv4 } from 'uuid';

import { useNotification } from '../../../contexts/NotificationContext';
import {
  useUsersPageCreateUserMutation,
  UserCreateInput,
} from '../../../generated';
import { mapToUser } from '../utils/userMapper';

export type CaptureExceptionFunction = (error: Error) => void;

export type UseCreateUserResponse = {
  createUser: (
    input: Omit<UserCreateInput, 'id' | 'chocoUser'>
  ) => Promise<User>;
  isLoading: boolean;
};

export type UseCreateUserConfig = {
  captureException?: CaptureExceptionFunction;
};

export const useCreateUser = (
  config: UseCreateUserConfig
): UseCreateUserResponse => {
  const { addNotification } = useNotification();
  const [createUserMutation, { loading }] = useUsersPageCreateUserMutation();

  const createUser = useCallback(
    async (input: Omit<UserCreateInput, 'id' | 'chocoUser'>): Promise<User> => {
      try {
        // Format email for loginEmail (matching legacy formatInput logic)
        const formattedEmail = input.email
          ? input.email.replace(/\s/g, '').toLowerCase()
          : null;

        // Handle supplierId: send null if empty string (matching legacy removeBlanksFallbackToNull)
        const supplierId =
          input.supplierId && input.supplierId.trim() !== ''
            ? input.supplierId.trim()
            : null;

        const result = await createUserMutation({
          variables: {
            userInput: {
              ...input,
              id: uuidv4(), // Generate unique ID
              chocoUser: true,
              email: formattedEmail || null, // Send null if empty string
              phone: input.phone ? input.phone.replace(/\s/g, '') : null, // Ensure phone is cleaned like legacy formatPhoneNumber
              // Set loginEmail when email is provided (matching legacy behavior)
              loginEmail: formattedEmail || null,
              supplierId, // Handle null/empty supplierId
              loginMethod: input.loginMethod || null, // Include loginMethod from input
              createdContext: 'userCreatedFromAdmin',
            },
          } as any, // Temporary: types need regeneration after removing withRoles from mutation
        });

        if (!result.data?.userCreate) {
          throw new Error('Failed to create user');
        }

        const createdUser = result.data.userCreate;

        // Map to generic User type using shared mapper
        const user = mapToUser(createdUser);

        addNotification({
          title: 'Success',
          message: 'User created successfully',
          type: 'success',
        });

        return user;
      } catch (error) {
        let errorMessage = 'Failed to create user. Please try again.';

        if (error instanceof ApolloError && error.graphQLErrors?.length > 0) {
          errorMessage = error.graphQLErrors[0].message;
        } else if (error instanceof Error) {
          errorMessage = error.message;
        }

        addNotification({
          type: 'danger',
          title: 'User Creation Failed',
          message: errorMessage,
        });

        throw error;
      }
    },
    [createUserMutation, addNotification]
  );

  return {
    createUser,
    isLoading: loading,
  };
};
